import { Component, ElementRef, HostListener, OnDestroy, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable, catchError, merge, filter, finalize, of, map, switchMap, tap, take, shareReplay, skip, first } from 'rxjs';

import { LobbyFilterLimit } from './features/lobby/enums/lobby-filter-limit';
import { LobbyFilterSpeed } from './features/lobby/enums/lobby-filter-speed';
import { environment } from 'src/environments/environment';
import { MatDialog, MatDialogContainer, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';

import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialogModule } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import * as ConfigActions from '@app/store/features/config/config.actions';
import * as ConfigSelectors from '@app/store/features/config/config.selectors';
import * as WebSocketSelectors from '@app/store/features/web-socket/web-socket.selectors';

import * as AuthSelectors from '@app/store/features/auth/auth.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar, MatSnackBarModule, MatSnackBarRef } from '@angular/material/snack-bar';
import { WsService } from './services/ws.service';
import { InitLoaderComponent } from './components/init-loader/init-loader.component';
import { Overlay, OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ButtonComponent } from './components/button/button.component';
import * as AuthActions from '@app/store/features/auth/auth.actions';
import { Assets, Application, Container, Sprite } from 'pixi.js';
import * as GameHorizontal from '@app/features/game/consts/game-horizontal.const';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PageService } from './services/page.service';
import { ConnectionStatus } from './enums/connection-status';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { StateStatus } from './enums/state-status';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

const angularMaterialModules = [
  MatSlideToggleModule,
  MatExpansionModule,
  MatDialogModule,

  MatSnackBarModule,

  OverlayModule
]
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'app-root' },
  imports: [
    CommonModule,
    RouterModule,
    InitLoaderComponent,
    TranslateModule,
    SpinnerComponent,
    ...angularMaterialModules
  ]
})
export class AppComponent implements OnDestroy {

  gameConstData = GameHorizontal

  getGameInitialized$ = this.store.pipe(select(ConfigSelectors.getGameInitialized));
  getDefaultAssetsLoaded$ = this.store.pipe(select(ConfigSelectors.getDefaultAssetsLoaded));

  wsConnetion = new BehaviorSubject<void>(undefined);
  isOpen = false;


  darkMainBackground$: Observable<boolean>

  domainSettings$ = this.store.pipe(
    select(ConfigSelectors.getDomainSettings),
    filter(domainSettings => !!domainSettings),
    map((domainSettings) => domainSettings!),
    take(1),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  mainBackgroundUrl$ = this.domainSettings$.pipe(
    map(domainSettings => `${environment.dataSource}/assets/${domainSettings?.code}/login/login_bg.png`),
    shareReplay({ bufferSize: 1, refCount: true })
  )


  autoLogin$ = this.store.select(AuthSelectors.status).pipe(
    switchMap((status) => {
      if (status === StateStatus.autoLogin) {
        return merge(
          of(status === StateStatus.autoLogin),
          this.appReadyToUse$.pipe(
            filter(appReadyToUse => appReadyToUse === true),
            first(),
            map(() => false)
          ))
      }

      return of(false)
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  )






  layoutHandsetLandscape$ = this._breakpointObserver.observe([
    Breakpoints.HandsetLandscape,
  ]);

  connectionStatus$ = this.store.select(WebSocketSelectors.getConnectionStatus);
  appReadyToUse$ = this.store.pipe(select(ConfigSelectors.getAppReadyToUse));

  isOffline$ = this.connectionStatus$.pipe(
    filter(connectionStatus => connectionStatus === ConnectionStatus.Offline),
    switchMap(() => merge(
      of(true),
      this.appReadyToUse$.pipe(
        filter(appReadyToUse => appReadyToUse === true),
        first(),
        map(() => false)
      ))),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  constructor(private readonly _breakpointObserver: BreakpointObserver, private pageService: PageService, private translate: TranslateService, public dialog: MatDialog, private store: Store<{ count: number }>, private _ws: WsService) {
    this.darkMainBackground$ = this.pageService.page$.pipe(
      map(page => {
        return page.includes('hand-history') || page.includes('table-sessions') || page.includes('hand-sessions')
      })
    )

    // Register available languages (removed 'de' - no German translation file exists)
    this.translate.addLangs(['en', 'mn']);
    this.translate.setDefaultLang('en');
    
    // Initialize language based on saved preference or browser locale
    this.initializeLanguage();


    // document.addEventListener('touchmove', this.touchMoveHandler, { passive: false });

  }

  /**
   * Initialize language based on saved preference or browser locale detection
   */
  private initializeLanguage(): void {
    // Check if user has a saved language preference
    const savedLanguage = localStorage.getItem('preferredLanguage');
    
    if (savedLanguage && this.translate.getLangs().includes(savedLanguage)) {
      // Use saved preference
      this.translate.use(savedLanguage);
    } else {
      // Detect browser language
      const browserLang = this.translate.getBrowserLang();
      
      // Check if browser language is Mongolian
      if (browserLang === 'mn' || browserLang?.startsWith('mn-')) {
        this.translate.use('mn');
        localStorage.setItem('preferredLanguage', 'mn');
      } else {
        // Default to English
        this.translate.use('en');
        localStorage.setItem('preferredLanguage', 'en');
      }
    }
  }

  /**
   * Switch language and save preference
   */
  public switchLanguage(lang: string): void {
    if (this.translate.getLangs().includes(lang)) {
      this.translate.use(lang);
      localStorage.setItem('preferredLanguage', lang);
    }
  }

  private touchMoveHandler = (e: TouchEvent) => {
    const target = e.target as HTMLElement;
    let el: HTMLElement | null = target;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflowY = style.getPropertyValue('overflow-y');
      const overflowX = style.getPropertyValue('overflow-x');
      const isScrollable = overflowY === 'auto' || overflowY === 'scroll' ||
        overflowX === 'auto' || overflowX === 'scroll';
      if (isScrollable && el.scrollHeight > el.clientHeight) {
        return; // allow scroll
      }
      el = el.parentElement;
    }
    e.preventDefault();
  };

  ngOnDestroy() {
    document.removeEventListener('touchmove', this.touchMoveHandler);
  }
}